/**
 * Document fingerprint utility
 * Creates a stable fingerprint for text content that's resilient to minor variations
 * from PDF extraction or formatting differences
 */

/**
 * Normalize text by removing inconsistencies in spacing, case, and special characters
 * @param {string} text - Raw text input
 * @returns {string} - Normalized text
 */
const normalizeText = (text) => {
    return text
      .replace(/\s+/g, ' ')    // Replace multiple spaces/newlines with single space
      .trim()                  // Remove leading/trailing spaces
      .toLowerCase()           // Convert to lowercase
      .replace(/[^\w\s]/g, ' '); // Replace special chars with space
  };
  
  /**
   * Extract the most frequent significant words from text
   * @param {string} text - Normalized text
   * @param {number} count - Number of top words to return
   * @returns {string[]} - Array of most frequent words
   */
  const getFrequentWords = (text, count = 10) => {
    const words = text.split(' ');
    const wordCount = {};
    
    // Count word frequencies (only for words > 4 chars)
    words.forEach(word => {
      if (word.length > 4) {
        wordCount[word] = (wordCount[word] || 0) + 1;
      }
    });
    
    // Sort by frequency and get top words
    return Object.entries(wordCount)
      .sort(([, a], [, b]) => b - a)
      .slice(0, count)
      .map(([word]) => word);
  };
  
  /**
   * Create a document fingerprint that's stable across minor text variations
   * @param {string} text - Original document text
   * @returns {Object} - Document fingerprint
   */
  export const createDocumentFingerprint = (text) => {
    if (!text || typeof text !== 'string') {
      throw new Error('Invalid input: text must be a non-empty string');
    }
  
    const normalizedText = normalizeText(text);
    
    // Get document length for quick initial comparison
    const length = normalizedText.length;
    
    // If text is too short, use simpler fingerprint
    if (length < 300) {
      return {
        length,
        samples: {
          full: normalizedText
        },
        topWords: getFrequentWords(normalizedText, 5),
        timestamp: Date.now()
      };
    }
  
    // For longer texts, take samples from different positions
    const sampleSize = 100;
    const startSample = normalizedText.slice(0, sampleSize);
    const midPoint = Math.floor(length / 2);
    const middleSample = normalizedText.slice(
      midPoint - sampleSize/2,
      midPoint + sampleSize/2
    );
    const endSample = normalizedText.slice(-sampleSize);
  
    return {
      length,
      samples: {
        start: startSample,
        middle: middleSample,
        end: endSample
      },
      topWords: getFrequentWords(normalizedText, 10),
      timestamp: Date.now()
    };
  };
  
  /**
   * Calculate similarity between two document fingerprints
   * @param {Object} fp1 - First document fingerprint
   * @param {Object} fp2 - Second document fingerprint
   * @returns {number} - Similarity score (0-1)
   */
  export const calculateSimilarity = (fp1, fp2) => {
    let score = 0;
    
    // Check length similarity (max 0.1 points)
    const lengthDiff = Math.abs(fp1.length - fp2.length) / Math.max(fp1.length, fp2.length);
    if (lengthDiff < 0.1) {
      score += 0.1 * (1 - lengthDiff);
    }
  
    // Compare text samples (max 0.6 points)
    const compareSamples = (s1, s2) => {
      if (!s1 || !s2) return 0;
      const maxLength = Math.max(s1.length, s2.length);
      let matches = 0;
      for (let i = 0; i < Math.min(s1.length, s2.length); i++) {
        if (s1[i] === s2[i]) matches++;
      }
      return matches / maxLength;
    };
  
    // Handle both short and long text cases
    if (fp1.samples.full && fp2.samples.full) {
      score += 0.6 * compareSamples(fp1.samples.full, fp2.samples.full);
    } else {
      score += 0.2 * compareSamples(fp1.samples.start, fp2.samples.start);
      score += 0.2 * compareSamples(fp1.samples.middle, fp2.samples.middle);
      score += 0.2 * compareSamples(fp1.samples.end, fp2.samples.end);
    }
  
    // Compare frequent words (max 0.3 points)
    const commonWords = fp1.topWords.filter(word => 
      fp2.topWords.includes(word)
    );
    score += 0.3 * (commonWords.length / Math.max(fp1.topWords.length, fp2.topWords.length));
  
    return score;
  };